import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { FaRobot, FaSearch } from 'react-icons/fa';
import { FiPlus, FiX, FiCheck, FiCircle, FiClock, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus.ts';
import { useCloseOnEscape } from '../hooks/useCloseOnEscape.ts';
import { useTranslation } from '../contexts/TranslationContext.tsx';
import { PremiumRequiredModal } from '../components/PremiumRequiredModal.tsx';
import { API_BASE } from '../utils/apiEndpoints';
import {
  findTopicBySlug,
  problemPath,
  topicProblemsPath,
  SPECIAL_TOPIC,
} from '../utils/problemUrls.ts';
import { getTopicLandingH1, topicSeoLabelRu } from '../utils/seoTopics.ts';
import { useSeoMeta, defaultSiteDescription } from '../hooks/useSeoMeta.ts';
import './css/ProblemsList.css';

const DIFFICULTY_OPTIONS = ['Easy', 'Medium', 'Hard'] as const;
const STATUS_OPTIONS = [
  { value: 'All', label: 'Все', icon: null },
  { value: 'Solved', label: 'Решено', icon: 'check' },
  { value: 'Unsolved', label: 'Не решено', icon: 'circle' },
  { value: 'Attempted', label: 'Есть попытки', icon: 'clock' },
] as const;
const FREQUENCY_OPTIONS = [
  { value: 'Low', label: 'Низкая', bars: 1 },
  { value: 'Medium', label: 'Средняя', bars: 2 },
  { value: 'High', label: 'Высокая', bars: 3 },
] as const;
const SORT_OPTIONS = [
  { value: 'difficulty', label: 'По сложности' },
  { value: 'title', label: 'По названию' },
  { value: 'acceptance', label: 'По принятию' },
  { value: 'frequency', label: 'По частоте' },
  { value: 'status', label: 'По статусу' },
  { value: 'id', label: 'По умолчанию' },
] as const;

function orderTopicsForDisplay(topics: string[]): string[] {
  const rest: string[] = [];
  let pinned: string | null = null;
  for (const topic of topics) {
    if (topic === SPECIAL_TOPIC) pinned = topic;
    else rest.push(topic);
  }
  return pinned ? [...rest, pinned] : rest;
}

function tagsForTopic(
  topic: string,
  allTags: string[],
  tagsByTopic: Record<string, string[]>
): string[] {
  if (!topic || topic === 'All') return allTags;
  const scoped = tagsByTopic[topic];
  return Array.isArray(scoped) ? scoped : allTags;
}

type StatusFilter = (typeof STATUS_OPTIONS)[number]['value'];
type FrequencyFilter = 'All' | 'Low' | 'Medium' | 'High';
type SortOption = (typeof SORT_OPTIONS)[number]['value'];
type SortableColumn = Exclude<SortOption, 'id'>;
type SortDir = 'asc' | 'desc';

interface FilterDraft {
  topic: string;
  difficulty: string;
  status: StatusFilter;
  acceptanceMin: number;
  acceptanceMax: number;
  frequency: FrequencyFilter;
  sortBy: SortOption;
  sortDir: SortDir;
  tags: string[];
}

const DEFAULT_FILTER_DRAFT: FilterDraft = {
  topic: 'All',
  difficulty: 'All',
  status: 'All',
  acceptanceMin: 0,
  acceptanceMax: 100,
  frequency: 'All',
  sortBy: 'id',
  sortDir: 'asc',
  tags: [],
};

const PROBLEMS_LIST_FILTERS_STORAGE_KEY = 'nn_problems_list_filters';

interface StoredProblemsListFilters {
  topic: string;
  difficulty: string;
  status: StatusFilter;
  acceptanceMin: number;
  acceptanceMax: number;
  frequency: FrequencyFilter;
  sortBy: SortOption;
  sortDir: SortDir;
  tags: string[];
  currentPage: number;
  searchTerm: string;
}

function isStatusFilter(value: unknown): value is StatusFilter {
  return typeof value === 'string' && STATUS_OPTIONS.some((option) => option.value === value);
}

function isSortOption(value: unknown): value is SortOption {
  return typeof value === 'string' && SORT_OPTIONS.some((option) => option.value === value);
}

function isSortDir(value: unknown): value is SortDir {
  return value === 'asc' || value === 'desc';
}

function isFrequencyFilter(value: unknown): value is FrequencyFilter {
  return value === 'All' || value === 'Low' || value === 'Medium' || value === 'High';
}

function readStoredProblemsListFilters(): StoredProblemsListFilters {
  const defaults: StoredProblemsListFilters = {
    topic: DEFAULT_FILTER_DRAFT.topic,
    difficulty: DEFAULT_FILTER_DRAFT.difficulty,
    status: DEFAULT_FILTER_DRAFT.status,
    acceptanceMin: DEFAULT_FILTER_DRAFT.acceptanceMin,
    acceptanceMax: DEFAULT_FILTER_DRAFT.acceptanceMax,
    frequency: DEFAULT_FILTER_DRAFT.frequency,
    sortBy: DEFAULT_FILTER_DRAFT.sortBy,
    sortDir: DEFAULT_FILTER_DRAFT.sortDir,
    tags: [],
    currentPage: 1,
    searchTerm: '',
  };

  try {
    const raw = localStorage.getItem(PROBLEMS_LIST_FILTERS_STORAGE_KEY);
    if (!raw) return defaults;

    const parsed = JSON.parse(raw) as Partial<StoredProblemsListFilters>;
    const acceptanceMin = Number(parsed.acceptanceMin);
    const acceptanceMax = Number(parsed.acceptanceMax);
    const currentPage = Number(parsed.currentPage);

    return {
      topic: typeof parsed.topic === 'string' && parsed.topic.trim() ? parsed.topic : defaults.topic,
      difficulty: typeof parsed.difficulty === 'string' ? parsed.difficulty : defaults.difficulty,
      status: isStatusFilter(parsed.status) ? parsed.status : defaults.status,
      acceptanceMin: Number.isFinite(acceptanceMin)
        ? Math.min(100, Math.max(0, acceptanceMin))
        : defaults.acceptanceMin,
      acceptanceMax: Number.isFinite(acceptanceMax)
        ? Math.min(100, Math.max(0, acceptanceMax))
        : defaults.acceptanceMax,
      frequency: isFrequencyFilter(parsed.frequency) ? parsed.frequency : defaults.frequency,
      sortBy: isSortOption(parsed.sortBy) ? parsed.sortBy : defaults.sortBy,
      sortDir: isSortDir(parsed.sortDir) ? parsed.sortDir : defaults.sortDir,
      tags: Array.isArray(parsed.tags)
        ? parsed.tags.filter((tag): tag is string => typeof tag === 'string' && tag.trim().length > 0)
        : defaults.tags,
      currentPage: Number.isFinite(currentPage) && currentPage >= 1
        ? Math.trunc(currentPage)
        : defaults.currentPage,
      searchTerm: typeof parsed.searchTerm === 'string' ? parsed.searchTerm : defaults.searchTerm,
    };
  } catch {
    return defaults;
  }
}

function writeStoredProblemsListFilters(filters: StoredProblemsListFilters): void {
  try {
    localStorage.setItem(PROBLEMS_LIST_FILTERS_STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // ignore quota / private mode
  }
}

function parseAcceptance(value: string): number {
  const n = parseInt(String(value).replace('%', ''), 10);
  return Number.isFinite(n) ? n : 0;
}

function normalizeFrequency(value: string | undefined): FrequencyFilter | null {
  const v = String(value || '').trim().toLowerCase();
  if (!v) return null;
  if (v.includes('низ') || v === 'low') return 'Low';
  if (v.includes('сред') || v === 'medium') return 'Medium';
  if (v.includes('выс') || v === 'high') return 'High';
  return null;
}

function difficultyRank(d: string): number {
  if (d === 'Easy') return 1;
  if (d === 'Medium') return 2;
  if (d === 'Hard') return 3;
  return 0;
}

function frequencyRank(value: string | undefined): number {
  const freq = normalizeFrequency(value);
  if (freq === 'Low') return 1;
  if (freq === 'Medium') return 2;
  if (freq === 'High') return 3;
  return 0;
}

function statusRank(status: string): number {
  if (status === 'Unsolved') return 0;
  if (status === 'Attempted') return 1;
  if (status === 'Solved') return 2;
  return 0;
}

function compareProblems(
  a: Problem,
  b: Problem,
  sortBy: SortOption,
  sortDir: SortDir,
  locale: string
): number {
  const dir = sortDir === 'desc' ? -1 : 1;
  switch (sortBy) {
    case 'difficulty':
      return dir * (difficultyRank(a.difficulty) - difficultyRank(b.difficulty));
    case 'title':
      return dir * a.title.localeCompare(b.title, locale);
    case 'acceptance':
      return dir * (parseAcceptance(a.acceptance) - parseAcceptance(b.acceptance));
    case 'frequency':
      return dir * (frequencyRank(a.frequency) - frequencyRank(b.frequency));
    case 'status':
      return dir * (statusRank(a.status) - statusRank(b.status));
    default:
      return a.id - b.id;
  }
}

function SortableHeader({
  column,
  label,
  className,
  sortBy,
  sortDir,
  onCycle,
}: {
  column: SortableColumn;
  label: string;
  className: string;
  sortBy: SortOption;
  sortDir: SortDir;
  onCycle: (column: SortableColumn) => void;
}) {
  const active = sortBy === column;
  const ariaSort = !active ? 'none' : sortDir === 'desc' ? 'descending' : 'ascending';
  return (
    <div className={className} aria-sort={ariaSort}>
      <button
        type="button"
        className={`table-sort-btn${active ? ' is-active' : ''}`}
        onClick={() => onCycle(column)}
      >
        <span>{label}</span>
        <span className="table-sort-arrows" aria-hidden>
          <FiChevronUp className={active && sortDir === 'asc' ? 'is-on' : undefined} />
          <FiChevronDown className={active && sortDir === 'desc' ? 'is-on' : undefined} />
        </span>
      </button>
    </div>
  );
}

function buildServerParams(
  filters: Pick<FilterDraft, 'topic' | 'difficulty' | 'status' | 'tags'>,
  page: number,
  search: string,
  lang: string
): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.difficulty !== 'All') params.append('difficulty', filters.difficulty);
  if (filters.topic !== 'All') params.append('topic', filters.topic);
  if (search) params.append('search', search);
  if (filters.status !== 'All') params.append('status', filters.status);
  if (filters.tags.length > 0) params.append('tags', filters.tags.join(','));
  params.append('page', page.toString());
  params.append('lang', lang || 'en');
  return params;
}

function hasActiveFilters(filters: FilterDraft): boolean {
  return (
    filters.topic !== 'All'
    || filters.difficulty !== 'All'
    || filters.status !== 'All'
    || filters.acceptanceMin > 0
    || filters.acceptanceMax < 100
    || filters.frequency !== 'All'
    || filters.tags.length > 0
  );
}

const BANNER_SLIDES = [
  { to: '/subscription', className: 'banner-content--0', labelKey: 'problemsList.bannerSubscription' },
  { to: '/promo/development', className: 'banner-content--1', labelKey: 'problemsList.bannerDevelopment' },
] as const;
const BANNER_COUNT = BANNER_SLIDES.length;
const BANNER_AUTO_MS = 5000;

interface Problem {
  id: number;
  title: string;
  slug?: string | null;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  topic?: string;
  status: 'Solved' | 'Attempted' | 'Unsolved';
  acceptance: string;
  frequency: string;
  tags: string[];
  premium: boolean;
  is_debug_task?: boolean;
  is_sql_sandbox?: boolean;
  /** Только для ответа, если залогинен admin */
  admin_only?: boolean;
}


function ProblemsList() {
  const navigate = useNavigate();
  const { topicOrId: topicFromPath } = useParams<{ topicOrId?: string }>();
  const { user } = useAuth();
  const { hasActiveSubscription } = useSubscriptionStatus();
  const { currentLanguage, t } = useTranslation();
  const initialFiltersRef = useRef(readStoredProblemsListFilters());
  const initialFilters = initialFiltersRef.current;
  const [activeBanner, setActiveBanner] = useState(0);
  const [bannerPaused, setBannerPaused] = useState(false);
  const [bannerEpoch, setBannerEpoch] = useState(0);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [activeDifficulty, setActiveDifficulty] = useState(initialFilters.difficulty);
  const [activeStatus, setActiveStatus] = useState<StatusFilter>(initialFilters.status);
  const [activeAcceptanceMin, setActiveAcceptanceMin] = useState(initialFilters.acceptanceMin);
  const [activeAcceptanceMax, setActiveAcceptanceMax] = useState(initialFilters.acceptanceMax);
  const [activeFrequency, setActiveFrequency] = useState<FrequencyFilter>(initialFilters.frequency);
  const [activeSortBy, setActiveSortBy] = useState<SortOption>(initialFilters.sortBy);
  const [activeSortDir, setActiveSortDir] = useState<SortDir>(initialFilters.sortDir);
  const [activeTags, setActiveTags] = useState<string[]>(initialFilters.tags);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [filterDraft, setFilterDraft] = useState<FilterDraft>(DEFAULT_FILTER_DRAFT);
  const [previewTotal, setPreviewTotal] = useState<number | null>(null);
  const [showTagPicker, setShowTagPicker] = useState(false);
  // Тема всегда из URL (/problems → All, /problems/sql → SQL). Не из localStorage —
  // иначе при «Все» после remount/навигации снова подтягивалась старая тема.
  const [activeTopic, setActiveTopic] = useState<string>('All');
  const [topics, setTopics] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(initialFilters.currentPage);
  const prevTopicPathRef = useRef<string | null>(null);
  const [searchQuery, setSearchQuery] = useState(initialFilters.searchTerm);
  const [searchTerm, setSearchTerm] = useState(initialFilters.searchTerm);
  const [problems, setProblems] = useState<Problem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [tagsByTopic, setTagsByTopic] = useState<Record<string, string[]>>({});
  const isInitialLoad = useRef(true);
  const tagPickerRef = useRef<HTMLDivElement>(null);

  const appliedFilters = useMemo<FilterDraft>(() => ({
    topic: activeTopic,
    difficulty: activeDifficulty,
    status: activeStatus,
    acceptanceMin: activeAcceptanceMin,
    acceptanceMax: activeAcceptanceMax,
    frequency: activeFrequency,
    sortBy: activeSortBy,
    sortDir: activeSortDir,
    tags: activeTags,
  }), [
    activeTopic, activeDifficulty, activeStatus,
    activeAcceptanceMin, activeAcceptanceMax, activeFrequency, activeSortBy, activeSortDir, activeTags,
  ]);

  const fetchTagCatalog = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/problems/topics_and_tags`, { credentials: 'include' });
      if (!response.ok) return;
      const data = await response.json();
      if (Array.isArray(data.tags)) setAllTags(data.tags);
      if (data.tags_by_topic && typeof data.tags_by_topic === 'object' && !Array.isArray(data.tags_by_topic)) {
        const next: Record<string, string[]> = {};
        for (const [topic, list] of Object.entries(data.tags_by_topic as Record<string, unknown>)) {
          if (!Array.isArray(list)) continue;
          next[topic] = list.filter((tag): tag is string => typeof tag === 'string' && tag.trim().length > 0);
        }
        setTagsByTopic(next);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const fetchProblems = useCallback(async () => {

    try {
      setLoading(true);
      setError(null);

    
      const params = buildServerParams(
        {
          topic: activeTopic,
          difficulty: activeDifficulty,
          status: activeStatus,
          tags: activeTags,
        },
        currentPage,
        searchTerm,
        currentLanguage || 'en'
      );
      const response = await fetch(`${API_BASE}/api/problems/problems_get?${params.toString()}`, {
        credentials: 'include'
      });
      if (!response.ok) {

        throw new Error(`HTTP error! status: ${response.status}`);

      }
      const data = await response.json();
      setProblems(data.problems);
      setTotalPages(data.totalPages);
      setTotalCount(typeof data.total === 'number' ? data.total : 0);
      if (Array.isArray(data.topics)) setTopics(orderTopicsForDisplay(data.topics));
      else setTopics([]);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
      setProblems([]);
    } finally {
      isInitialLoad.current = false;
      setLoading(false);
    }
  }, [currentPage, activeDifficulty, activeTopic, activeStatus, activeTags, searchTerm, currentLanguage]);

  useEffect(() => {
    fetchProblems();
  }, [fetchProblems]);

  useEffect(() => {
    writeStoredProblemsListFilters({
      topic: activeTopic,
      difficulty: activeDifficulty,
      status: activeStatus,
      acceptanceMin: activeAcceptanceMin,
      acceptanceMax: activeAcceptanceMax,
      frequency: activeFrequency,
      sortBy: activeSortBy,
      sortDir: activeSortDir,
      tags: activeTags,
      currentPage,
      searchTerm,
    });
  }, [
    activeTopic,
    activeDifficulty,
    activeStatus,
    activeAcceptanceMin,
    activeAcceptanceMax,
    activeFrequency,
    activeSortBy,
    activeSortDir,
    activeTags,
    currentPage,
    searchTerm,
  ]);

  useEffect(() => {
    fetchTagCatalog();
  }, [fetchTagCatalog]);

  useCloseOnEscape(showFiltersModal, () => setShowFiltersModal(false));

  useEffect(() => {
    if (!showFiltersModal) {
      setPreviewTotal(null);
      setShowTagPicker(false);
      return undefined;
    }
    const ac = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        const params = buildServerParams(filterDraft, 1, searchTerm, currentLanguage || 'en');
        const response = await fetch(`${API_BASE}/api/problems/problems_get?${params.toString()}`, {
          credentials: 'include',
          signal: ac.signal,
        });
        if (!response.ok) return;
        const data = await response.json();
        setPreviewTotal(typeof data.total === 'number' ? data.total : null);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
      }
    }, 250);
    return () => {
      window.clearTimeout(timer);
      ac.abort();
    };
  }, [showFiltersModal, filterDraft, searchTerm, currentLanguage]);

  useEffect(() => {
    if (!showTagPicker) return undefined;
    const onDocClick = (e: MouseEvent) => {
      if (tagPickerRef.current && !tagPickerRef.current.contains(e.target as Node)) {
        setShowTagPicker(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showTagPicker]);

  const openFiltersModal = () => {
    setFilterDraft({ ...appliedFilters });
    fetchTagCatalog();
    setShowFiltersModal(true);
  };

  const selectTopic = useCallback((topic: string) => {
    setActiveTopic(topic);
    setCurrentPage(1);
    navigate(topicProblemsPath(topic));
  }, [navigate]);

  const applyFilters = () => {
    selectTopic(filterDraft.topic);
    setActiveDifficulty(filterDraft.difficulty);
    setActiveStatus(filterDraft.status);
    setActiveAcceptanceMin(filterDraft.acceptanceMin);
    setActiveAcceptanceMax(filterDraft.acceptanceMax);
    setActiveFrequency(filterDraft.frequency);
    setActiveSortBy(filterDraft.sortBy);
    setActiveSortDir(filterDraft.sortDir);
    setActiveTags(filterDraft.tags);
    setShowFiltersModal(false);
  };

  const resetFilterDraft = () => {
    setFilterDraft({ ...DEFAULT_FILTER_DRAFT });
  };

  const updateFilterDraft = <K extends keyof FilterDraft>(key: K, value: FilterDraft[K]) => {
    setFilterDraft((prev) => ({ ...prev, [key]: value }));
  };

  const cycleColumnSort = (column: SortableColumn) => {
    let nextSortBy: SortOption = column;
    let nextSortDir: SortDir = 'asc';
    if (activeSortBy !== column) {
      nextSortBy = column;
      nextSortDir = 'asc';
    } else if (activeSortDir === 'asc') {
      nextSortBy = column;
      nextSortDir = 'desc';
    } else {
      nextSortBy = 'id';
      nextSortDir = 'asc';
    }
    setActiveSortBy(nextSortBy);
    setActiveSortDir(nextSortDir);
    setFilterDraft((prev) => ({ ...prev, sortBy: nextSortBy, sortDir: nextSortDir }));
  };

  const selectDraftTopic = (topic: string) => {
    setFilterDraft((prev) => {
      const allowed = tagsByTopic[topic];
      const tags =
        topic === 'All' || !Array.isArray(allowed)
          ? prev.tags
          : prev.tags.filter((tag) => allowed.includes(tag));
      return { ...prev, topic, tags };
    });
  };

  const pickerTags = useMemo(
    () =>
      tagsForTopic(filterDraft.topic, allTags, tagsByTopic).filter(
        (tag) => !filterDraft.tags.includes(tag)
      ),
    [filterDraft.topic, filterDraft.tags, allTags, tagsByTopic]
  );

  const toggleDraftDifficulty = (value: typeof DIFFICULTY_OPTIONS[number]) => {
    updateFilterDraft('difficulty', filterDraft.difficulty === value ? 'All' : value);
  };

  const toggleDraftFrequency = (value: 'Low' | 'Medium' | 'High') => {
    updateFilterDraft('frequency', filterDraft.frequency === value ? 'All' : value);
  };

  const removeDraftTag = (tag: string) => {
    updateFilterDraft('tags', filterDraft.tags.filter((t) => t !== tag));
  };

  const addDraftTag = (tag: string) => {
    if (filterDraft.tags.includes(tag)) return;
    updateFilterDraft('tags', [...filterDraft.tags, tag]);
    setShowTagPicker(false);
  };

  const resetBannerAutoplay = () => setBannerEpoch((e) => e + 1);

  useEffect(() => {
    if (bannerPaused || BANNER_COUNT <= 1) return undefined;
    const id = window.setInterval(() => {
      setActiveBanner((prev) => (prev + 1) % BANNER_COUNT);
    }, BANNER_AUTO_MS);
    return () => window.clearInterval(id);
  }, [bannerPaused, bannerEpoch]);

  // URL — источник истины для темы (/problems = All, /problems/:topic = тема)
  useEffect(() => {
    // Ждём список тем, пока в URL есть slug темы
    if (topicFromPath && topics.length === 0) return;

    const resolved = topicFromPath
      ? findTopicBySlug(topicFromPath, topics)
      : 'All';

    if (topicFromPath && !resolved) {
      navigate('/problems', { replace: true });
      return;
    }

    const nextTopic = resolved || 'All';
    const pathKey = topicFromPath ?? '';

    // Сброс пагинации при смене темы через чипы/URL (не при первом монтировании)
    if (prevTopicPathRef.current !== null && prevTopicPathRef.current !== pathKey) {
      setCurrentPage(1);
    }
    prevTopicPathRef.current = pathKey;

    setActiveTopic((prev) => (prev === nextTopic ? prev : nextTopic));
  }, [topicFromPath, topics, navigate]);

  const listSeoTitle =
    currentLanguage === 'ru'
      ? activeTopic && activeTopic !== 'All'
        ? `Задачи по ${topicSeoLabelRu(activeTopic)} онлайн с автопроверкой — Sciplex`
        : 'Задачи по программированию онлайн — практика с автопроверкой | Sciplex'
      : activeTopic && activeTopic !== 'All'
        ? `${activeTopic} practice problems online — Sciplex`
        : 'Programming practice problems online — Sciplex';
  const listSeoDescription =
    currentLanguage === 'ru'
      ? activeTopic && activeTopic !== 'All'
        ? `Каталог задач по ${topicSeoLabelRu(activeTopic)} на Sciplex: практика в браузере, мгновенная проверка решений, теория и прогресс.`
        : defaultSiteDescription('ru')
      : activeTopic && activeTopic !== 'All'
        ? `Browse ${activeTopic} problems on Sciplex with instant checking and progress tracking.`
        : defaultSiteDescription('en');
  useSeoMeta({
    title: listSeoTitle,
    description: listSeoDescription,
    canonicalPath: topicProblemsPath(activeTopic),
  });

  const landingH1 = getTopicLandingH1(
    activeTopic && activeTopic !== 'All' ? activeTopic : 'All',
    currentLanguage
  );

  const getDifficultyColor = (difficulty: string) => {

    switch (difficulty) {

      case 'Easy': return 'var(--nn-difficulty-easy)';

      case 'Medium': return 'var(--nn-difficulty-medium)';

      case 'Hard': return 'var(--nn-difficulty-hard)';

      default: return '#000'; 

    }

  };



  const filteredProblems = useMemo(() => {
    // Поиск по названию — только на сервере (searchTerm), не по текущей странице.
    let list = problems.filter((problem) => {
      const acceptance = parseAcceptance(problem.acceptance);
      if (acceptance < activeAcceptanceMin || acceptance > activeAcceptanceMax) return false;

      if (activeFrequency !== 'All') {
        const freq = normalizeFrequency(problem.frequency);
        if (freq !== activeFrequency) return false;
      }

      return true;
    });

    list = [...list].sort((a, b) => {
      if (activeTopic === 'SQL') {
        const aSandbox = a.is_sql_sandbox ? 1 : 0;
        const bSandbox = b.is_sql_sandbox ? 1 : 0;
        if (aSandbox !== bSandbox) return bSandbox - aSandbox;
      }
      return compareProblems(a, b, activeSortBy, activeSortDir, currentLanguage || 'ru');
    });

    return list;
  }, [
    problems, activeAcceptanceMin, activeAcceptanceMax,
    activeFrequency, activeSortBy, activeSortDir, activeTopic, currentLanguage,
  ]);

  const applySearch = useCallback(() => {
    setSearchTerm(searchQuery.trim());
    setCurrentPage(1);
  }, [searchQuery]);

  useEffect(() => {
    if (searchQuery.trim() === searchTerm) return undefined;
    const timer = window.setTimeout(() => {
      setSearchTerm(searchQuery.trim());
      setCurrentPage(1);
    }, 400);
    return () => window.clearTimeout(timer);
  }, [searchQuery, searchTerm]);

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      applySearch();
    }
  };

  const handleSearchClick = () => {
    applySearch();
  };

  const handlePrevBanner = () => {
    setActiveBanner((prev) => (prev - 1 + BANNER_COUNT) % BANNER_COUNT);
    resetBannerAutoplay();
  };

  const handleNextBanner = () => {
    setActiveBanner((prev) => (prev + 1) % BANNER_COUNT);
    resetBannerAutoplay();
  };

  const handleBannerSelect = (index: number) => {
    setActiveBanner(index);
    resetBannerAutoplay();
  };



  /* Полноэкранный лоадер только при первой загрузке, чтобы не было белой вспышки при смене фильтров */
  if (loading && isInitialLoad.current) return <div className="loading">{t('problemsList.loading')}</div>;

  if (error) return <div className="error">Error: {error}</div>;



  return (

    <div className='ProblemsList'>
      <div className="problems-list-content">
      <div className="banner-section">
        <div className="banner-container">
          <div
            className="ad-banner"
            role="region"
            aria-label={t('problemsList.bannerRegion')}
            onMouseEnter={() => setBannerPaused(true)}
            onMouseLeave={() => setBannerPaused(false)}
            onFocusCapture={() => setBannerPaused(true)}
            onBlurCapture={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                setBannerPaused(false);
              }
            }}
          >
            <div className="banner-slider">
              <div
                className="banner-content-wrapper"
                style={{ transform: `translateX(-${activeBanner * 100}%)` }}
              >
                {BANNER_SLIDES.map((slide) => (
                  <Link
                    key={slide.to}
                    to={slide.to}
                    className={`banner-content ${slide.className}`}
                    aria-label={t(slide.labelKey)}
                  />
                ))}
              </div>
            </div>
            <button
              type="button"
              className="banner-arrow left"
              onClick={handlePrevBanner}
              aria-label="Предыдущий баннер"
            >
              ‹
            </button>
            <button
              type="button"
              className="banner-arrow right"
              onClick={handleNextBanner}
              aria-label="Следующий баннер"
            >
              ›
            </button>
            <div className="banner-pagination" role="tablist" aria-label="Слайды баннера">
              {Array.from({ length: BANNER_COUNT }).map((_, index) => (
                <button
                  key={index}
                  type="button"
                  role="tab"
                  aria-selected={activeBanner === index}
                  className={`banner-pagination-segment ${activeBanner === index ? 'is-active' : ''}`}
                  onClick={() => handleBannerSelect(index)}
                  aria-label={`Баннер ${index + 1} из ${BANNER_COUNT}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

        <header className="problems-seo-intro">
          <h1 className="problems-seo-intro__title">{landingH1}</h1>
        </header>

        <div className="problems-header">
          <div className="problems-header-left">
            <div className="topic-filter-list">
              <Link
                to={topicProblemsPath('All')}
                className={`topic-chip ${activeTopic === 'All' ? 'active' : ''}`}
                aria-current={activeTopic === 'All' ? 'page' : undefined}
              >
                {t('problemsList.difficultyAll')}
              </Link>
              {topics.map((tp) => (
                <Link
                  key={tp}
                  to={topicProblemsPath(tp)}
                  className={`topic-chip ${activeTopic === tp ? 'active' : ''}`}
                  aria-current={activeTopic === tp ? 'page' : undefined}
                >
                  {tp}
                </Link>
              ))}
            </div>
            <button
              type="button"
              className="filter-icon-btn"
              onClick={openFiltersModal}
              title="Фильтры"
              aria-label="Открыть фильтры"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
              {(hasActiveFilters(appliedFilters)) && <span className="filter-badge" />}
            </button>
          </div>
          <div className="problems-header-right">
            <button
              type="button"
              className="add-problem-btn"
              onClick={() => navigate('/problems/suggest')}
              title={t('problemsList.addProblem')}
              aria-label={t('problemsList.addProblem')}
            >
              <FiPlus size={17} strokeWidth={1.75} aria-hidden />
            </button>
            <div className="search-container">
              <div className="search-field">
                <span className="search-field-icon" aria-hidden>
                  <FaSearch />
                </span>
                <input
                  type="search"
                  className="search-field-input"
                  placeholder={t('problemsList.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  autoComplete="off"
                  aria-label={t('problemsList.searchPlaceholder')}
                />
              </div>
              <button type="button" className="search-button" onClick={handleSearchClick}>
                {t('problemsList.search')}
              </button>
            </div>
          </div>
        </div>

        {showFiltersModal && (
          <div className="filters-modal-overlay" onClick={() => setShowFiltersModal(false)} role="dialog" aria-modal="true" aria-label="Фильтры задач">
            <div className="filters-modal filters-modal--advanced" onClick={(e) => e.stopPropagation()}>
              <div className="filters-modal-header">
                <h3>Фильтры задач</h3>
                <button type="button" className="filters-modal-close" onClick={() => setShowFiltersModal(false)} aria-label="Закрыть">
                  <FiX size={18} />
                </button>
              </div>

              <div className="filters-modal-body">
                <section className="fm-section">
                  <span className="fm-section-label">Направление</span>
                  <div className="fm-chips">
                    <button
                      type="button"
                      className={`fm-chip ${filterDraft.topic === 'All' ? 'is-active' : ''}`}
                      onClick={() => selectDraftTopic('All')}
                    >
                      {t('problemsList.difficultyAll')}
                    </button>
                    {topics.map((tp) => (
                      <button
                        key={tp}
                        type="button"
                        className={`fm-chip ${filterDraft.topic === tp ? 'is-active' : ''}`}
                        onClick={() => selectDraftTopic(tp)}
                      >
                        {tp}
                      </button>
                    ))}
                  </div>
                </section>

                <div className="fm-row fm-row--2">
                  <section className="fm-section">
                    <span className="fm-section-label">Сложность</span>
                    <div className="fm-chips">
                      {DIFFICULTY_OPTIONS.map((d) => (
                        <button
                          key={d}
                          type="button"
                          className={`fm-chip fm-chip--difficulty fm-chip--${d.toLowerCase()} ${filterDraft.difficulty === d ? 'is-active' : ''}`}
                          onClick={() => toggleDraftDifficulty(d)}
                        >
                          <span className="fm-difficulty-dot" aria-hidden />
                          {t(`problemsList.difficulty${d}`)}
                        </button>
                      ))}
                    </div>
                  </section>

                  <section className="fm-section">
                    <span className="fm-section-label">Статус</span>
                    <div className="fm-segmented">
                      {STATUS_OPTIONS.map(({ value, label, icon }) => (
                        <button
                          key={value}
                          type="button"
                          className={`fm-segment ${filterDraft.status === value ? 'is-active' : ''}`}
                          onClick={() => updateFilterDraft('status', value)}
                        >
                          {icon === 'check' && <FiCheck size={13} aria-hidden />}
                          {icon === 'circle' && <FiCircle size={13} aria-hidden />}
                          {icon === 'clock' && <FiClock size={13} aria-hidden />}
                          {label}
                        </button>
                      ))}
                    </div>
                  </section>
                </div>

                <div className="fm-row fm-row--2">
                  <section className="fm-section">
                    <span className="fm-section-label">{t('problemsList.acceptance')}</span>
                    <div className="fm-range">
                      <div
                        className="fm-range-track"
                        style={{
                          ['--fm-from' as string]: String(filterDraft.acceptanceMin),
                          ['--fm-to' as string]: String(filterDraft.acceptanceMax),
                        }}
                      >
                        <div className="fm-range-fill" />
                        <input
                          type="range"
                          min={0}
                          max={100}
                          value={filterDraft.acceptanceMin}
                          className="fm-range-input fm-range-input--min"
                          onChange={(e) => {
                            const next = Math.min(Number(e.target.value), filterDraft.acceptanceMax);
                            updateFilterDraft('acceptanceMin', next);
                          }}
                          aria-label="Минимальный процент принятия"
                        />
                        <input
                          type="range"
                          min={0}
                          max={100}
                          value={filterDraft.acceptanceMax}
                          className="fm-range-input fm-range-input--max"
                          onChange={(e) => {
                            const next = Math.max(Number(e.target.value), filterDraft.acceptanceMin);
                            updateFilterDraft('acceptanceMax', next);
                          }}
                          aria-label="Максимальный процент принятия"
                        />
                      </div>
                      <div className="fm-range-labels">
                        <span>{filterDraft.acceptanceMin}%</span>
                        <span>{filterDraft.acceptanceMax}%</span>
                      </div>
                    </div>
                  </section>

                  <section className="fm-section">
                    <span className="fm-section-label">{t('problemsList.frequency')}</span>
                    <div className="fm-frequency">
                      {FREQUENCY_OPTIONS.map(({ value, label, bars }) => (
                        <button
                          key={value}
                          type="button"
                          className={`fm-frequency-btn ${filterDraft.frequency === value ? 'is-active' : ''}`}
                          onClick={() => toggleDraftFrequency(value)}
                        >
                          <span className="fm-frequency-bars" aria-hidden>
                            {Array.from({ length: bars }).map((_, i) => (
                              <span key={i} className="fm-frequency-bar" />
                            ))}
                          </span>
                          {label}
                        </button>
                      ))}
                    </div>
                  </section>
                </div>

                <div className="fm-row">
                  <section className="fm-section">
                    <span className="fm-section-label">Теги</span>
                    <div className="fm-tags">
                      {filterDraft.tags.map((tag) => (
                        <button
                          key={tag}
                          type="button"
                          className="fm-tag"
                          onClick={() => removeDraftTag(tag)}
                          aria-label={`Убрать тег ${tag}`}
                        >
                          {tag}
                          <FiX size={12} aria-hidden />
                        </button>
                      ))}
                      <div className="fm-tag-add-wrap" ref={tagPickerRef}>
                        <button
                          type="button"
                          className="fm-tag-add"
                          onClick={() => setShowTagPicker((v) => !v)}
                          aria-label="Добавить тег"
                        >
                          <FiPlus size={14} />
                        </button>
                        {showTagPicker && pickerTags.length > 0 && (
                          <div className="fm-tag-picker">
                            {pickerTags.map((tag) => (
                                <button key={tag} type="button" onClick={() => addDraftTag(tag)}>
                                  {tag}
                                </button>
                              ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </section>
                </div>
              </div>

              <div className="filters-modal-footer">
                <p className="fm-found">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                    <path d="M12 2l2.4 7.4H22l-6 4.6 2.3 7L12 16.8 5.7 21l2.3-7-6-4.6h7.6L12 2z" />
                  </svg>
                  Найдено <strong>{previewTotal ?? totalCount}</strong> задач
                </p>
                <div className="fm-footer-actions">
                  <button type="button" className="fm-btn fm-btn--ghost" onClick={resetFilterDraft}>
                    Сбросить
                  </button>
                  <button type="button" className="fm-btn fm-btn--primary" onClick={applyFilters}>
                    Применить
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className={`problems-table-wrapper ${loading ? 'is-loading' : ''}`}>
        <div className="problems-table">

          <div className="table-header">
            <SortableHeader
              column="status"
              label={t('problemsList.status')}
              className="status-col"
              sortBy={activeSortBy}
              sortDir={activeSortDir}
              onCycle={cycleColumnSort}
            />
            <SortableHeader
              column="title"
              label={t('problemsList.title')}
              className="title-col"
              sortBy={activeSortBy}
              sortDir={activeSortDir}
              onCycle={cycleColumnSort}
            />
            <SortableHeader
              column="difficulty"
              label={t('problemsList.difficulty')}
              className="difficulty-col"
              sortBy={activeSortBy}
              sortDir={activeSortDir}
              onCycle={cycleColumnSort}
            />
            <SortableHeader
              column="acceptance"
              label={t('problemsList.acceptance')}
              className="acceptance-col"
              sortBy={activeSortBy}
              sortDir={activeSortDir}
              onCycle={cycleColumnSort}
            />
            <SortableHeader
              column="frequency"
              label={t('problemsList.frequency')}
              className="frequency-col"
              sortBy={activeSortBy}
              sortDir={activeSortDir}
              onCycle={cycleColumnSort}
            />
          </div>

          

          {filteredProblems.map(problem => (

            <div key={problem.id} className="problem-row">

              <div className="status-col">
                {problem.premium && !hasActiveSubscription ? (
                  <span className="problem-lock" title="Premium" aria-hidden>🔒</span>
                ) : (
                  <>
                    {problem.status === 'Solved' && <span className="solved">✓</span>}
                    {problem.status === 'Attempted' && <span className="attempted">!</span>}
                  </>
                )}
              </div>

              <div className="title-col">
                <Link
                  to={problemPath(problem)}
                  className="problem-title-link"
                  onClick={(e) => {
                    if (problem.premium && !hasActiveSubscription) {
                      e.preventDefault();
                      setShowPremiumModal(true);
                    }
                  }}
                >
                  {problem.title}
                  {problem.is_debug_task ? (
                    <span className="problem-debug-badge" title="Задача на поиск ошибки">
                      <FaRobot />
                      <span className="problem-debug-q">?</span>
                    </span>
                  ) : null}
                  {user?.role === 'admin' && problem.admin_only ? (
                    <span className="problem-draft-badge" title={t('problemsList.draftBadge')}>
                      {t('problemsList.draftBadge')}
                    </span>
                  ) : null}
                </Link>
              </div>

              <div className="difficulty-col" style={{ color: getDifficultyColor(problem.difficulty) }}>
                {t(`problemsList.difficulty${problem.difficulty}`)}
              </div>

              <div className="acceptance-col">{problem.acceptance}</div>

              <div className="frequency-col">{problem.frequency}</div>

            </div>

          ))}

        </div>
        </div>

        <div className="pagination">
          <button
            type="button"
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          >
            Back
          </button>

          <input
            className="page-input"
            type="number"
            min={1}
            max={totalPages}
            value={currentPage}
            onChange={(e) => {
              const raw = Number(e.target.value);
              if (!Number.isFinite(raw)) return;
              const next = Math.min(totalPages, Math.max(1, Math.trunc(raw)));
              setCurrentPage(next);
            }}
            aria-label="Номер страницы"
          />

          <button
            type="button"
            disabled={currentPage >= totalPages}
            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          >
            Next
          </button>
        </div>
      </div>

      {showPremiumModal && (
        <PremiumRequiredModal onClose={() => setShowPremiumModal(false)} />
      )}

    </div>

  );

}

export default ProblemsList;